const MAX_CONSOLE_ERRORS = 10;

interface ErrorEntry {
  source: 'error' | 'unhandledrejection';
  message: string;
  stack?: string;
  timestamp: string;
  filename?: string;
  line?: number;
  column?: number;
}

const consoleErrorBuffer: ErrorEntry[] = [];
const diagnosticsStartTime = Date.now();
let listenersRegistered = false;
let errorHandler: ((event: ErrorEvent) => void) | undefined;
let rejectionHandler: ((event: PromiseRejectionEvent) => void) | undefined;

function sanitizeErrorMessage(message: string | null | undefined): string | null | undefined {
  if (!message) return message;
  
  return String(message)
    .replace(/[\w-]{24,}/g, '[REDACTED_TOKEN]')
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]')
    .replace(/file:\/\/\/[^\s]*/g, 'file:///[PATH]')
    .replace(/\/Users\/[^\s\/]+/g, '/Users/[USER]')
    .replace(/C:\\Users\\[^\s\\]+/g, 'C:\\Users\\[USER]');
}

function formatErrorEvent(event: ErrorEvent | PromiseRejectionEvent): ErrorEntry | null {
  if (!event) return null;

  if (event.type === 'unhandledrejection') {
    const rejectionEvent = event as PromiseRejectionEvent;
    const reason = rejectionEvent.reason;
    const rawMessage = reason instanceof Error ? reason.message : String(reason);
    return {
      source: 'unhandledrejection',
      message: sanitizeErrorMessage(rawMessage) || 'Unknown rejection',
      stack: reason instanceof Error ? sanitizeErrorMessage(reason.stack) || undefined : undefined,
      timestamp: new Date().toISOString(),
    };
  }

  const errorEvent = event as ErrorEvent;
  return {
    source: 'error',
    message: sanitizeErrorMessage(errorEvent.message || 'Unknown error') || 'Unknown error',
    filename: errorEvent.filename,
    line: errorEvent.lineno,
    column: errorEvent.colno,
    stack: errorEvent.error?.stack ? sanitizeErrorMessage(errorEvent.error.stack) || undefined : undefined,
    timestamp: new Date().toISOString(),
  };
}

function pushErrorEntry(entry: ErrorEntry | null): void {
  if (!entry) return;
  consoleErrorBuffer.push(entry);

  if (consoleErrorBuffer.length > MAX_CONSOLE_ERRORS) {
    consoleErrorBuffer.splice(0, consoleErrorBuffer.length - MAX_CONSOLE_ERRORS);
  }
}

export function initializeDiagnosticsListeners(): void {
  if (listenersRegistered || typeof window === 'undefined') return;

  errorHandler = (event: ErrorEvent) => {
    pushErrorEntry(formatErrorEvent(event));
  };

  rejectionHandler = (event: PromiseRejectionEvent) => {
    pushErrorEntry(formatErrorEvent(event));
  };

  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);

  listenersRegistered = true;
}

export function cleanupDiagnosticsListeners(): void {
  if (!listenersRegistered || typeof window === 'undefined') return;
  
  if (errorHandler) window.removeEventListener('error', errorHandler);
  if (rejectionHandler) window.removeEventListener('unhandledrejection', rejectionHandler);
  
  listenersRegistered = false;
  consoleErrorBuffer.length = 0;
}

interface StorageEstimate {
  quota: number | null;
  usage: number | null;
  error?: string;
}

async function getStorageEstimate(): Promise<StorageEstimate | null> {
  if (!navigator.storage?.estimate) {
    return null;
  }

  try {
    const estimate = await navigator.storage.estimate();
    return {
      quota: estimate.quota ?? null,
      usage: estimate.usage ?? null,
    };
  } catch (error) {
    return {
      quota: null,
      usage: null,
      error: error instanceof Error ? error.message : 'Unable to estimate storage quota',
    };
  }
}

async function getVoicesCount(): Promise<number> {
  if (!window.speechSynthesis?.getVoices) {
    return 0;
  }

  let voices = window.speechSynthesis.getVoices();
  if (voices.length > 0) {
    return voices.length;
  }

  return new Promise((resolve) => {
    const handler = () => {
      clearTimeout(timeout);
      window.speechSynthesis.removeEventListener('voiceschanged', handler);
      resolve(window.speechSynthesis.getVoices().length);
    };

    const timeout = setTimeout(() => {
      window.speechSynthesis.removeEventListener('voiceschanged', handler);
      resolve(window.speechSynthesis.getVoices().length);
    }, 100);

    window.speechSynthesis.addEventListener('voiceschanged', handler);
    window.speechSynthesis.getVoices();
  });
}

export async function collectDiagnosticData(gameSnapshot: any = {}, preferences: any = {}): Promise<any> {
  initializeDiagnosticsListeners();

  const storageEstimate = await getStorageEstimate();
  const voicesCount = await getVoicesCount();
  const navigationEntry = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
  const pageLoadTime = navigationEntry?.loadEventEnd
    ? Math.max(0, Math.round(navigationEntry.loadEventEnd))
    : null;

  return {
    game: {
      state: gameSnapshot.gameState || 'unknown',
      difficulty: preferences.difficulty || 'unknown',
      gradeLevel: preferences.gradeLevel || 'unknown',
      roundsPlayed: gameSnapshot.roundsPlayed || 0,
      currentStreak: gameSnapshot.streak || 0,
      score: gameSnapshot.score || 0,
      usedWordsCount: gameSnapshot.usedWordsCount || 0,
      masteredWordsCount: gameSnapshot.masteredWordsCount || 0,
      recentPerformance: Array.isArray(gameSnapshot.recentPerformance)
        ? gameSnapshot.recentPerformance.slice(-10)
        : [],
    },
    browser: {
      ttsSupported: 'speechSynthesis' in window,
      availableVoicesCount: voicesCount,
      speechRecognitionSupported:
        'SpeechRecognition' in window ||
        'webkitSpeechRecognition' in window,
      language: navigator.language,
      platform: navigator.platform,
      onLine: navigator.onLine,
      userAgent: navigator.userAgent,
    },
    display: {
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio,
      touchSupported: 'ontouchstart' in window,
    },
    storage: storageEstimate,
    performance: {
      pageLoadTimeMs: pageLoadTime,
      timeOnPageMs: Date.now() - diagnosticsStartTime,
      memoryUsedBytes: (performance as any).memory?.usedJSHeapSize ?? null,
      memoryLimitBytes: (performance as any).memory?.jsHeapSizeLimit ?? null,
    },
    errors: {
      recentConsoleErrors: consoleErrorBuffer.slice(-5),
    },
  };
}
